import React, { useState, useEffect, useRef } from 'react';

const BadgeAdmin = () => {
  const [badges, setBadges] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]); // Changed to array for multiple selection
  const [selectedBadge, setSelectedBadge] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [newBadge, setNewBadge] = useState({
    name: '',
    description: '',
    icon: 'star',
    color: '#3B82F6'
  });

  const userDropdownRef = useRef(null);

  useEffect(() => {
    fetchBadges();
    fetchUsers();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchBadges = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/badges`);
      const data = await response.json();
      setBadges(data);
    } catch (error) {
      console.error('Error fetching badges:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  // Handle user selection
  const handleUserSelect = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Handle select all functionality
  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length && filteredUsers.length > 0) {
      // If all filtered users are selected, deselect all
      const filteredUserIds = filteredUsers.map(user => user._id);
      setSelectedUsers(prev => prev.filter(id => !filteredUserIds.includes(id)));
    } else {
      // Select all filtered users
      const filteredUserIds = filteredUsers.map(user => user._id);
      setSelectedUsers(prev => {
        const newSelected = [...prev];
        filteredUserIds.forEach(id => {
          if (!newSelected.includes(id)) {
            newSelected.push(id);
          }
        });
        return newSelected;
      });
    }
  };

  // Get selected user names for display
  const getSelectedUserNames = () => {
    return selectedUsers.map(userId => {
      const user = users.find(u => u._id === userId);
      return user ? user.name : '';
    }).filter(name => name);
  };

  const createBadge = async (e) => {
    e.preventDefault();
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const token = userData?.token;
      if (!token) {
        alert('No authentication token found. Please login again.');
        return;
      }
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/badges`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newBadge)
      });
      if (response.ok) {
        setNewBadge({ name: '', description: '', icon: 'star', color: '#3B82F6' });
        setShowCreateForm(false);
        fetchBadges();
        alert('Badge created successfully!');
      } else {
        const error = await response.json();
        console.error('Badge creation error:', error);
        alert(error.error || 'Error creating badge');
      }
    } catch (error) {
      console.error('Error creating badge:', error);
      alert('Error creating badge');
    }
  };

  const awardBadge = async (e) => {
    e.preventDefault();
    if (selectedUsers.length === 0 || !selectedBadge) return;

    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const token = userData?.token;
      
      
      // Award badge to each selected user
      const promises = selectedUsers.map(userId => 
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/badges/award`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            userId: userId,
            badgeId: selectedBadge
          })
        })
      );

      const responses = await Promise.all(promises);
      const successCount = responses.filter(response => response.ok).length;
      
      if (successCount === selectedUsers.length) {
        alert(`Badge awarded successfully to ${successCount} user(s)!`);
        setSelectedUsers([]);
        setSelectedBadge('');
      } else if (successCount > 0) {
        alert(`Badge awarded to ${successCount} out of ${selectedUsers.length} users. Some may have already had this badge.`);
        setSelectedUsers([]);
        setSelectedBadge('');
      } else {
        alert('Error awarding badge to users');
      }
    } catch (error) {
      console.error('Error awarding badge:', error);
      alert('Error awarding badge');
    }
  };

  const deleteBadge = async (badgeId) => {
    if (!window.confirm('Are you sure? This will remove the badge from all users who have it.')) {
      return;
    }
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const token = userData?.token;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/badges/${badgeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        fetchBadges();
        alert('Badge deleted successfully!');
      } else {
        const error = await response.json();
        alert(error.error || 'Error deleting badge');
      }
    } catch (error) {
      console.error('Delete exception:', error);
      alert('Error deleting badge');
    }
  };

  const getBadgeIcon = (icon) => {
    switch (icon) {
      case 'star': return '⭐';
      case 'trophy': return '🏆';
      case 'medal': return '🏅';
      case 'fire': return '🔥';
      case 'heart': return '❤️';
      case 'crown': return '👑';
      case 'gem': return '💎';
      case 'rocket': return '🚀';
      case 'check': return '✅';
      default: return '🏅';
    }
  };

  return (
    <div className="space-y-8">
      {/* Award Badge Section */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-bold mb-4">Award Badge to Users</h2>
        <form onSubmit={awardBadge} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Multi-select User Dropdown */}
            <div>
              <label className="block text-sm font-medium mb-1">Select Users</label>
              <div className="relative" ref={userDropdownRef}>
                <div
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  className="w-full p-2 border rounded-md cursor-pointer bg-white min-h-[2.5rem] flex items-center justify-between"
                >
                  <div className="flex-1">
                    {selectedUsers.length === 0 ? (
                      <span className="text-gray-500">Choose users...</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {getSelectedUserNames().slice(0, 2).map((name, index) => (
                          <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm">
                            {name}
                          </span>
                        ))}
                        {selectedUsers.length > 2 && (
                          <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-md text-sm">
                            +{selectedUsers.length - 2} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                
                {isUserDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-hidden">
                    {/* Search Input */}
                    <div className="p-2 border-b">
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        className="w-full p-2 border rounded-md text-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    
                    {/* Select All Option */}
                    {filteredUsers.length > 0 && (
                      <div
                        onClick={handleSelectAll}
                        className="p-2 hover:bg-gray-100 cursor-pointer border-b flex items-center gap-2"
                      >
                        <input
                          type="checkbox"
                          checked={filteredUsers.length > 0 && filteredUsers.every(user => selectedUsers.includes(user._id))}
                          onChange={() => {}}
                          className="rounded"
                        />
                        <span className="font-medium text-blue-600">
                          Select All ({filteredUsers.length})
                        </span>
                      </div>
                    )}
                    
                    {/* User List */}
                    <div className="max-h-48 overflow-y-auto">
                      {filteredUsers.length === 0 ? (
                        <div className="p-4 text-gray-500 text-center">
                          {userSearchTerm ? 'No users found' : 'No users available'}
                        </div>
                      ) : (
                        filteredUsers.map(user => (
                          <div
                            key={user._id}
                            onClick={() => handleUserSelect(user._id)}
                            className="p-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                          >
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user._id)}
                              onChange={() => {}}
                              className="rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {user.name}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {user.email} • {user.role}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Select Badge</label>
              <select
                value={selectedBadge}
                onChange={(e) => setSelectedBadge(e.target.value)}
                className="w-full p-2 border rounded-md"
                required
              >
                <option value="">Choose a badge...</option>
                {badges.map(badge => (
                  <option key={badge._id} value={badge._id}>
                    {getBadgeIcon(badge.icon)} {badge.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button
            type="submit"
            className="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600 disabled:opacity-50"
            disabled={selectedUsers.length === 0 || !selectedBadge}
          >
            Award Badge to {selectedUsers.length} User{selectedUsers.length !== 1 ? 's' : ''}
          </button>
        </form>
      </div>

      {/* Rest of your existing code remains the same... */}
      {/* Create Badge Section */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Create New Badge</h2>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
          >
            {showCreateForm ? 'Cancel' : 'Create Badge'}
          </button>
        </div>
        {showCreateForm && (
          <form onSubmit={createBadge} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Badge Name</label>
                <input
                  type="text"
                  value={newBadge.name}
                  onChange={(e) => setNewBadge({...newBadge, name: e.target.value})}
                  className="w-full p-2 border rounded-md"
                  placeholder="e.g., First Post, Helpful Student"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Icon</label>
                <select
                  value={newBadge.icon}
                  onChange={(e) => setNewBadge({...newBadge, icon: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="star">⭐ Star</option>
                  <option value="trophy">🏆 Trophy</option>
                  <option value="medal">🏅 Medal</option>
                  <option value="fire">🔥 Fire</option>
                  <option value="heart">❤️ Heart</option>
                  <option value="crown">👑 Crown</option>
                  <option value="gem">💎 Gem</option>
                  <option value="rocket">🚀 Rocket</option>
                  <option value="check">✅ Check</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={newBadge.description}
                onChange={(e) => setNewBadge({...newBadge, description: e.target.value})}
                className="w-full p-2 border rounded-md"
                placeholder="What did they do to earn this badge?"
                rows="3"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Badge Color</label>
              <div className="flex items-center gap-4">
                <input
                  type="color"
                  value={newBadge.color}
                  onChange={(e) => setNewBadge({...newBadge, color: e.target.value})}
                  className="w-16 h-10 border rounded-md cursor-pointer"
                />
                <span className="text-sm text-gray-600">{newBadge.color}</span>
              </div>
            </div>
            <button
              type="submit"
              className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600"
            >
              Create Badge
            </button>
          </form>
        )}
      </div>

      {/* Existing Badges */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-bold mb-4">All Badges ({badges.length})</h2>
        {badges.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No badges created yet. Create your first badge above!</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {badges.map(badge => (
              <div key={badge._id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg"
                    style={{ backgroundColor: badge.color }}
                  >
                    {getBadgeIcon(badge.icon)}
                  </div>
                  <button
                    onClick={() => deleteBadge(badge._id)}
                    className="text-red-500 hover:text-red-700 text-sm bg-red-50 px-2 py-1 rounded"
                  >
                    Delete
                  </button>
                </div>
                <h3 className="font-semibold text-lg mb-1">{badge.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{badge.description}</p>
                <div className="text-xs text-gray-400">
                  Created: {new Date(badge.createdAt).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BadgeAdmin;

